import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "AML Reclamation Program | How revenue works",
  "includeToc": true,
  "description": "The Abandoned Mine Land (AML) Reclamation Program uses fees paid by present-day coal mining companies to reclaim coal mines abandoned before 1977.",
  "tags": ["How revenue works", "AML", "Abandoned mine lands", "AML fund", "Reclamation", "Coal", "Mining"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const GlossaryTerm = makeShortcode("GlossaryTerm");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/GOMESA/how-revenue-works/"
      }}>{`How revenue works /`}</a></p>

    <h1 {...{
      "id": "Abandoned-Mine-Land-Reclamation-Program",
      "style": {
        "position": "relative"
      }
    }}>{`Abandoned Mine Land Reclamation Program`}</h1>
    <p>{`The Abandoned Mine Land (AML) Reclamation Program uses fees paid by present-day coal mining companies to `}<GlossaryTerm mdxType="GlossaryTerm">{`reclaim`}</GlossaryTerm>{` coal mines abandoned before 1977. This makes these areas safer for people and the environment. The Surface Mining Control and Reclamation Act (SMCRA) of 1977, as amended, created this program. Provisions of the act set standards for today's coal companies as they reclaim past mining areas while they mine others. Coal companies post bonds to cover the cost if unable to reclaim current coal mines.`}</p>
    <h2 {...{
      "id": "Abandoned-mine-land-areas",
      "style": {
        "position": "relative"
      }
    }}>{`Abandoned mine land areas`}</h2>
    <p>{`Abandoned mines pose risks to people and the environment. They can contaminate ground water, emit toxic waste, and cause injury when unsteady infrastructures collapse.`}</p>
    <h3 {...{
      "id": "Priority-reclamation-sites",
      "style": {
        "position": "relative"
      }
    }}>{`Priority reclamation sites`}</h3>
    <p>{`State and tribal AML programs rank abandoned mine land problems on a priority scale of 1 to 3 as defined by federal law. Mine pollution problems ranked 1 and 2 are high priority and are addressed first, with `}<a href="https://www.law.cornell.edu/uscode/text/30/1233">{`a few exceptions `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Priority 1`}</strong>{`: Protection of public health, safety, and property from extreme danger of adverse effects of coal mining practices pre-1977. This protection includes restoration of land, water, and the environment.`}</li>
      <li parentName="ul"><strong parentName="li">{`Priority 2`}</strong>{`: Protection of public health and safety from adverse effects of coal mining practices pre-1977. This protection includes restoration of land, water, and the environment.`}</li>
      <li parentName="ul"><strong parentName="li">{`Priority 3`}</strong>{`: Restoration of land and water resources and the environment previously degraded by adverse effects of coal mining practices pre-1977.`}</li>
    </ul>
    <h3 {...{
      "id": "Areas-undergoing-or-requiring-reclamation",
      "style": {
        "position": "relative"
      }
    }}>{`Areas undergoing or requiring reclamation`}</h3>
    <p>{`The federal AML Reclamation Program distributes funds to the state and tribal AML programs. These funds go to the remaining Priority 1 and 2 sites.`}</p>
    <br />
State and tribal AML programs use funds to prepare for reclamation projects, including:
    <ul>
      <li parentName="ul">{`permitting processes`}</li>
      <li parentName="ul">{`environmental assessments`}</li>
      <li parentName="ul">{`site surveys`}</li>
      <li parentName="ul">{`development of reclamation plans`}</li>
    </ul>
    <p>{`After completing project preparation, funds are used for construction to reclaim the site.`}</p>
    <br />
Office of Surface Mining, Reclamation and Enforcement (OSMRE) is required to report measurable goals to Congress. One of their key measures is the number of abandoned mine land acres reclaimed.
    <h2 {...{
      "id": "Revenue-and-disbursements",
      "style": {
        "position": "relative"
      }
    }}>{`Revenue and disbursements`}</h2>
    <p>{`Fees paid by coal mine operators fund the AML Reclamation Program. Companies pay a `}<a href="https://www.gpo.gov/fdsys/granule/USCODE-2011-title30/USCODE-2011-title30-chap25-subchapIV-sec1232">{`per-ton fee to OSMRE `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <h3 {...{
      "id": "Fund-distribution",
      "style": {
        "position": "relative"
      }
    }}>{`Fund distribution`}</h3>
    <p>{`Since passage of the Tax Relief and Health Care Act of 2006, distribution of AML grants are mandatory. Prior to 2006, distribution was subject to Congress' annual `}<GlossaryTerm mdxType="GlossaryTerm">{`appropriations`}</GlossaryTerm>{` process.`}</p>
    <br />
The amount states and tribes receive each year in annual payments varies depending on the fees collected during the previous fiscal year. (For example, this fiscal year's fees fund next fiscal year's grants.)
    <br />
The statute requires the government to distribute AML Reclamation Program funds collected during the previous fiscal year as follows:
    <ul>
      <li parentName="ul"><strong parentName="li">{`State and tribal share:`}</strong>{` Non-certified states receive 50% of the AML revenue originating from coal production in their states. This accounts for 50% of overall distribution of AML fees in a given year. `}<a href="#cert">{`Certified states`}</a>{` receive amounts equal to their respective share grant totals sourced from the Treasury's General Fund.`}</li>
      <li parentName="ul"><strong parentName="li">{`Historic coal share:`}</strong>{` 30% of overall AML fees go toward Historic Coal Grants for non-certified states. OSMRE allocates Historic Coal grants based on each state's percentage of coal tonnage produced prior to 1977. Congress created Historic Coal grants so that states with large numbers of abandoned mines, but little current coal production, would not be left without funds to reclaim them.`}</li>
      <li parentName="ul"><strong parentName="li">{`Federal expense share`}</strong>{` (including minimum program make-up grants): 20% of AML fees goes toward federal expenditures. These funds must first be used to fund Minimum Program Make-Up grants to non-certified states. These ensure that non-certified states receive at least $3 million a year or the amount necessary to reclaim their remaining high priority AML areas, whichever is lower. OSMRE uses the remaining amount to fund operations, emergency projects, and other efforts.`}</li>
    </ul>
    <p>{`Review the `}<a href="https://www.osmre.gov/resources/grants-resources">{`OSMRE annual AML grant distribution reports `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` to track annual state grants.`}</p>
    <h3 {...{
      "id": "State-and-tribal-spending",
      "style": {
        "position": "relative"
      }
    }}>{`State and tribal spending`}</h3>
    <p>{`State and tribal AML programs use funds to prepare for reclamation through:`}</p>
    <ul>
      <li parentName="ul">{`an extensive permitting process, `}</li>
      <li parentName="ul">{`environmental assessments, `}</li>
      <li parentName="ul">{`site surveys, and `}</li>
      <li parentName="ul">{`the development of reclamation plans. `}</li>
    </ul>
    <p>{`Generally, states and tribes must reclaim all Priority 1 and 2 coal sites before using AML funds on Priority 3 coal sites, but several exceptions exist.`}</p>
    <br />
In addition to scheduled reclamation projects, <a href="https://www.gpo.gov/fdsys/pkg/USCODE-2010-title30/pdf/USCODE-2010-title30-chap25-subchapIV-sec1231.pdf">states and tribes can use AML funds for related efforts <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>, such as:
    <ul>
      <li parentName="ul">{`covering the costs of administering and enforcing their AML programs`}</li>
      <li parentName="ul">{`funding emergency AML reclamation projects`}</li>
      <li parentName="ul">{`setting aside annual funds (up to 30%) for projects related to acid mine drainage`}</li>
    </ul>
    <p>{`OSMRE also maintains a list of `}<a href="https://www.osmre.gov/contact-us/state-tribal-contacts">{`contacts for states, Indian tribes, and OSMRE offices `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
    <h3 id="cert">Certification</h3>
    <p>{`When a state or tribe has reclaimed high priority coal AML areas, `}<a href="https://www.law.cornell.edu/uscode/text/30/1240">{`they may be eligible for "certification." `}<OpenInNewIcon style={{
          fontSize: 20
        }} mdxType="OpenInNewIcon" /></a>{` Once certified, the source of their funds from OSMRE changes. Instead of receiving AML grants, they receive funds from the General Fund of the U.S. Treasury. These funds can be used for other purposes, including reclaiming abandoned hardrock mine sites. A certified state or tribe may still have abandoned coal mine areas to reclaim.`}</p>
    <br />
You can track certified states and tribes by reviewing the <a href="https://www.osmre.gov/resources/grants-resources">OSMRE annual AML grant distribution reports <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>.
    <h2 {...{
      "id": "The-AML-fund",
      "style": {
        "position": "relative"
      }
    }}>{`The AML fund`}</h2>
    <p>{`Prior to 2006, companies often paid more money into the AML fund each year than Congress chose to appropriate. A large, unspent balance grew over time. In 1990, Congress authorized the federal government to invest the unspent balance in U.S. Treasury Securities. Interest from AML fees pays for a portion of the costs for health care plans for the United Mine Workers of America.`}</p>
    <br />
Congress resolved to pay states' and tribes' unspent balance out in the form of "Prior Balance Replacement Grants," which pay states and tribes 50% of what companies operating in their jurisdictions paid into the AML Fund from 2008–2014.
    <br /><br />
The Office of Surface Mining and Reclamation (OSMRE) within the U.S. Department of the Interior provides regular updates regarding collections and distributions and annually publishes the <a href="https://www.osmre.gov/programs/abandoned-mine-land-awards-program">fund’s current status <OpenInNewIcon style={{
        fontSize: 20
      }} mdxType="OpenInNewIcon" /></a>.

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      